import { useCallback, useRef, useState, useEffect } from "react"

function csrfToken() {
  if (typeof document === "undefined") return ""
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
}

// Streams Guide-me responses via POST /app/intake_guide_chats. Returns
// helpers to send a new message and abort an in-flight stream.
export function useIntakeGuideStream() {
  const [isStreaming, setIsStreaming] = useState(false)
  const [currentChunk, setCurrentChunk] = useState("")
  const [error, setError] = useState(null)
  const abortRef = useRef(null)

  useEffect(() => {
    return () => abortRef.current?.abort()
  }, [])

  const sendMessage = useCallback(async ({ section, chatHistory, onDone }) => {
    abortRef.current?.abort()
    abortRef.current = new AbortController()
    setError(null)
    setCurrentChunk("")
    setIsStreaming(true)

    let assembled = ""
    try {
      const response = await fetch("/app/intake_guide_chats", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
          Accept: "text/event-stream",
        },
        body: JSON.stringify({ section, chat_history: chatHistory }),
        credentials: "same-origin",
        signal: abortRef.current.signal,
      })

      if (!response.ok) {
        setError("The assistant couldn't connect. Please try again.")
        setIsStreaming(false)
        return
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ""

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        let lineEnd
        while ((lineEnd = buffer.indexOf("\n")) !== -1) {
          const line = buffer.slice(0, lineEnd).trim()
          buffer = buffer.slice(lineEnd + 1)
          if (!line || line.startsWith(":")) continue
          if (!line.startsWith("data: ")) continue
          const data = line.slice(6)
          if (data === "[DONE]") {
            setIsStreaming(false)
            onDone?.(assembled)
            return
          }
          try {
            const parsed = JSON.parse(data)
            if (parsed.error) {
              setError(parsed.error)
              setIsStreaming(false)
              return
            }
            if (parsed.content) {
              assembled += parsed.content
              setCurrentChunk(assembled)
            }
          } catch {
            // ignore malformed
          }
        }
      }
      setIsStreaming(false)
      onDone?.(assembled)
    } catch (err) {
      if (err.name === "AbortError") return
      setError(err.message || "Connection error")
      setIsStreaming(false)
    }
  }, [])

  const abort = useCallback(() => {
    abortRef.current?.abort()
    setIsStreaming(false)
  }, [])

  return { isStreaming, currentChunk, error, sendMessage, abort }
}
